/**
 * CompactToolCallCard — shared compact inline tool-call surface.
 *
 * Lifted from SagaToolCallCard so any companion panel (Saga, Marcus, …) can
 * render the same Manus/Cursor-style pill: icon + verb + (optional context
 * hint), state-tinted border, click-to-expand raw output.
 *
 * Each surface plugs in its own resolvers:
 *   - resolveIcon(name)      → which lucide icon represents this tool
 *   - resolveVerb(name, in)  → the human label ("Highlight", "Loading brand"…)
 *   - resolveHint(name, in)  → a short input-derived hint shown after the verb
 *
 * Defaults: Briefcase icon, fallback verb (snake_case → Title Case), no hint.
 */
import React, { useState } from 'react';
import {
  Loader2,
  AlertCircle,
  Check,
  ChevronDown,
  Briefcase,
} from 'lucide-react';
import { cn } from '@/lib/utils';

type IconCmp = React.ComponentType<{ className?: string }>;

export interface CompactToolCallCardProps {
  name: string;
  input?: unknown;
  result: unknown;
  state: string;
  resolveIcon?: (name: string) => IconCmp;
  resolveVerb?: (name: string, input?: unknown) => string;
  resolveHint?: (name: string, input?: unknown) => string | undefined;
}

export function defaultFallbackVerb(toolName: string): string {
  return toolName
    .replace(/[_-]+/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());
}

export function CompactToolCallCard({
  name,
  input,
  result,
  state,
  resolveIcon,
  resolveVerb,
  resolveHint,
}: CompactToolCallCardProps) {
  const [expanded, setExpanded] = useState(false);
  const isLoading =
    state === 'input-streaming' || state === 'input-available' || state === 'loading';
  const isError = !!(result && typeof result === 'object' && (result as Record<string, unknown>).error);

  const Icon = resolveIcon ? resolveIcon(name) : Briefcase;
  const verb = resolveVerb ? resolveVerb(name, input) : defaultFallbackVerb(name);
  const hint = resolveHint ? resolveHint(name, input) : undefined;
  const canExpand = result != null;

  return (
    <div
      className={cn(
        'ds-micro max-w-full min-w-0 rounded-lg border transition-colors',
        isLoading
          ? 'bg-muted/20 border-violet-500/25'
          : isError
            ? 'bg-red-500/5 border-red-500/30'
            : 'bg-muted/15 border-border/40',
      )}
    >
      <button
        type="button"
        onClick={() => canExpand && setExpanded((v) => !v)}
        disabled={!canExpand}
        className={cn(
          'w-full min-w-0 flex items-center gap-2 px-2.5 py-1.5 text-left rounded-lg',
          canExpand ? 'hover:bg-muted/25 cursor-pointer' : 'cursor-default',
        )}
      >
        <Icon
          className={cn(
            'w-3.5 h-3.5 shrink-0',
            isLoading ? 'text-violet-300' : isError ? 'text-red-400' : 'text-foreground/60',
          )}
        />
        <span className="min-w-0 truncate font-medium text-foreground/85">{verb}</span>
        {hint && <span className="text-foreground/50 truncate min-w-0">· {hint}</span>}
        <span className="ml-auto flex items-center gap-1.5 shrink-0">
          {isLoading ? (
            <Loader2 className="w-3 h-3 animate-spin text-violet-300" />
          ) : isError ? (
            <AlertCircle className="w-3 h-3 text-red-400" />
          ) : (
            <Check className="w-3 h-3 text-emerald-400" />
          )}
          {canExpand && (
            <ChevronDown
              className={cn(
                'w-3 h-3 text-foreground/30 transition-transform',
                expanded && 'rotate-180',
              )}
            />
          )}
        </span>
      </button>
      {expanded && canExpand && (
        <div className="px-2.5 pb-2 -mt-0.5">
          <pre className="ds-micro text-foreground/55 overflow-auto max-h-32 p-2 rounded bg-muted/30 border border-border/30">
            {typeof result === 'string' ? result : JSON.stringify(result, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export default CompactToolCallCard;
